import type { InsuranceIndication } from '../../models/insuranceIndication'
import { InsuranceIndicationStatus, Vendor } from '../../models/insuranceTypes'

/** Same words the backend refuses with, so the tooltip and a refused press read alike. */
export const CORGI_SUBMITTED_MESSAGE =
  'Corgi already has this quote. Change it in the Corgi portal, then press Sync.'
export const CORGI_EDITED_MESSAGE = 'Changes since the last submit. Resubmit to send them.'
const DRAFT_ERROR_FALLBACK = 'Corgi refused the draft before submitting it.'

// Corgi is done with a quote in these statuses, so a new draft may replace it.
const ENDED_STATUSES = new Set(['expired', 'not_taken', 'cancelled', 'failed'])
// Being bought or bought: never ended, whatever its dates say.
const PURCHASE_STATUSES = new Set(['pending_payment', 'bound'])

export interface CorgiResubmitState {
  /** Set while Corgi holds the quote as submitted: Resubmit is disabled with this message. */
  blockedReason: string | null
  /** Corgi refused the draft before accepting it; the reason staff must fix. */
  draftError: string | null
  /** The application changed after the last submit attempt, so Resubmit would send something new. */
  editedSinceSubmit: boolean
}

/**
 * Resubmit rules for a Corgi card, from the markers the sync run stores.
 *
 * Mirrors resubmit_block_reason in corgi_v2_resubmit_rules.py. Metadata arrives
 * camelized, so the keys read here are the backend's in camelCase.
 */
export function corgiResubmitState(
  indication: InsuranceIndication,
  applicationEditedAt?: string | null
): CorgiResubmitState | null {
  if (indication.vendor !== Vendor.CORGI) return null
  const { metadata } = indication
  const expiresAt: string | undefined = metadata.corgiQuoteDates?.expiresAt
  const ended =
    !PURCHASE_STATUSES.has(metadata.corgiLastStatus) &&
    (ENDED_STATUSES.has(metadata.corgiLastStatus) ||
      !!metadata.corgiExpired ||
      (!!expiresAt && new Date(expiresAt).getTime() <= Date.now()))
  const blocked = !!metadata.corgiAcceptedQuoteId && !ended
  const lastAttempt: string | undefined = metadata.corgiLastSubmitAttemptAt
  return {
    blockedReason: blocked ? CORGI_SUBMITTED_MESSAGE : null,
    draftError:
      indication.status === InsuranceIndicationStatus.ERROR && metadata.corgiDraftError
        ? (metadata.error ?? DRAFT_ERROR_FALLBACK)
        : null,
    editedSinceSubmit:
      !blocked &&
      !!applicationEditedAt &&
      !!lastAttempt &&
      new Date(applicationEditedAt).getTime() > new Date(lastAttempt).getTime(),
  }
}

/** A Corgi quote the sync job reads, so staff can press Sync with Corgi on it. */
export function isCorgiSyncable(indication: InsuranceIndication): boolean {
  return indication.vendor === Vendor.CORGI && indication.metadata.corgiApiVersion === 'v2'
}

/**
 * Why a staff Sync looks needed, or null when Corgi looks up to date.
 *
 * Reads only markers the sync run stores. A change made in Corgi's portal shows
 * in none of them, so staff can still sync when this returns null.
 */
export function corgiSyncNeed(indication: InsuranceIndication): string | null {
  const { metadata, status } = indication
  const isOpen = status === InsuranceIndicationStatus.PENDING
  // A draft Corgi refused is fixed with Resubmit; Sync is for a quote Corgi holds.
  if (status === InsuranceIndicationStatus.ERROR && indication.vendorId && !metadata.corgiDraftError) {
    return 'This quote errored.'
  }
  if (isOpen && metadata.corgiChecksStoppedAt) return 'Cai stopped checking this quote.'
  const handoffAt: string | undefined = metadata.corgiPortalHandoffAt
  const answeredAt: string | undefined = metadata.corgiSyncAnsweredAt
  if (handoffAt && (!answeredAt || new Date(handoffAt).getTime() > new Date(answeredAt).getTime())) {
    return 'Staff alerted to upload in Corgi.'
  }
  return null
}
